import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { authAPI } from '../../../../services/redwolfSdkAPI';

import Modal from '../../../common/components/Modal';
import Spinner from '../../../common/components/Spinner';
import { addClasses } from '../../../../utils/eventListeners';
import { openNotification } from '../../../../store/commonSlice';
import { notification } from '../../../../utils/constants';
import { setShippingAddress } from '../../../../store/userSlice';

import { useGoogleAnalytics } from '../../../common/hooks/useGoogleAnalytics';
import useRequestRedwolfService from '../../../common/hooks/useRequestRedwolfService';
import useTechnicalErrorMessage from '../../../common/hooks/useTechnicalErrorMessage';
import * as Sentry from '@sentry/react';
import { checkNameFormat } from '../../../../utils/validations';
import { getToken } from '../../../../utils/localStorage';
import AccountSkeleton from '../../../common/skeletons/AccountSkeleton';

declare global {
	interface Window {
		pca: any;
	}
}

interface ShippingAddressPayload {
	firstName: string;
	lastName: string;
	phone: string;
	streetAddress: string;
	additionalAddress?: string;
	city: string;
	state: string;
	postalCode: string;
}

const ShippingAddress = () => {
	const {
		account: { shippingAddress }
	} = useSelector((state: any) => state.user);
	const [isShippingAddInfoModalOpen, setIsShippingAddInfoModalOpen] = useState(false);
	const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(true);
	const [isSpinnerLoading, setIsSpinnerLoading] = useState(false);
	const [isSkeletonLoading, setIsSkeletonLoading] = useState(false);
	const [requestRedwolfService] = useRequestRedwolfService();
	const [logGoogleAnalyticsEvent] = useGoogleAnalytics();
	const [showTechnicalErrorMessage] = useTechnicalErrorMessage();
	const dispatch = useDispatch();
	const token = getToken();

	const transaction = Sentry.startInactiveSpan({ name: 'Shipping Address' });

	const [contactForm, setContactForm] = useState({
		firstName: '',
		lastName: '',
		phone: ''
	});

	const [addressForm, setAddressForm] = useState({
		streetAddress: '',
		additionalAddress: '',
		city: '',
		state: '',
		postalCode: ''
	});

	const [formErrorMessages, setFormErrorMessages] = useState({
		isPhoneNoValid: true,
		isAddressValid: true
	});

	const firstNameRef = useRef<HTMLInputElement>(null);
	const lastNameRef = useRef<HTMLInputElement>(null);
	const phoneRef = useRef<HTMLInputElement>(null);
	const addressLineOneRef = useRef<HTMLInputElement>(null);
	const addressLineTwoRef = useRef<HTMLInputElement>(null);
	const cityRef = useRef<HTMLInputElement>(null);
	const stateRef = useRef<HTMLInputElement>(null);
	const zipCodeRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		addClasses();

		if (window.pca) {
			window.pca.on('load', function (_0: any, _1: any, control: any) {
				control.listen('populate', function (address: any) {
					populateAddress(address);
				});
			});

			if (window.pca.load) {
				window.pca.load();
			}
		}

		return () => {
			if (window.pca && window.pca.onq) {
				window.pca.onq = [];
			}
		};

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!shippingAddress) {
			setIsSkeletonLoading(true);
			if (token !== null) {
				requestRedwolfService(authAPI.getShippingAddress(token))
					.then((response: any) => {
						setIsSkeletonLoading(false);
						transaction && transaction.end();
						if (response.constructor.$metadata$.simpleName === 'Success' && response.data) {
							dispatch(setShippingAddress(response.data));
						}
					})
					.catch(() => {
						setIsSkeletonLoading(false);
						transaction && transaction.end();
					});
			}
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (shippingAddress) {
			setContactForm({
				firstName: shippingAddress.firstName,
				lastName: shippingAddress.lastName,
				phone: shippingAddress.phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
			});

			setAddressForm({
				streetAddress: shippingAddress.streetAddress,
				additionalAddress: shippingAddress.additionalAddress,
				city: shippingAddress.city,
				state: shippingAddress.state,
				postalCode: shippingAddress.postalCode
			});

			setIsSaveButtonDisabled(false);
		}
	}, [shippingAddress]);

	useEffect(() => {
		enableSaveButton();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [addressForm]);

	const handleKeyDown = (event: any) => {
		const { name, value, selectionStart, selectionEnd } = event.target;
		const RegexNumberSpace = /^[0-9\s]+$/i;
		const RegexNumber = /^[0-9]+$/i;

		if ((name === 'firstName' || name === 'lastName') && !checkNameFormat(event.key)) {
			event.preventDefault();
		} else if (
			name === 'phone' &&
			event.keyCode === 8 &&
			'-() '.includes(value[selectionStart - 1]) &&
			value[selectionStart]
		) {
			phoneRef.current &&
				phoneRef.current.setSelectionRange(selectionStart - 1, selectionStart - 1);
			event.preventDefault();
		} else if (
			name === 'phone' &&
			event.keyCode === 8 &&
			'-() '.includes(value[selectionStart]) &&
			RegexNumberSpace.test(value[selectionEnd])
		) {
			phoneRef.current && phoneRef.current.setSelectionRange(selectionStart, selectionStart);
			event.preventDefault();
		} else if (
			name === 'phone' &&
			'-() '.includes(value[selectionStart]) &&
			RegexNumber.test(value[selectionEnd])
		) {
			phoneRef.current && phoneRef.current.setSelectionRange(selectionStart, selectionStart);
			event.preventDefault();
		}
	};

	const restrictPhoneNo = (event: any) => {
		const numericRegExp = /^[0-9]+$/i;

		if (!numericRegExp.test(event.key)) {
			event.preventDefault();
		}
	};

	const handleChange = (event: any) => {
		const { name, value } = event.target;

		setAddressForm({ ...addressForm, [name]: value });

		if (name === 'firstName' || name === 'lastName') {
			setContactForm({ ...contactForm, [name]: value });
		} else if (name === 'phone' && value.length < 15) {
			setFormErrorMessages({ ...formErrorMessages, isPhoneNoValid: true });

			let shippingPhoneNo = value.replace(/[^\d]/g, '');
			const cursorPosition = event.target.selectionStart;

			if (shippingPhoneNo.length === 0) {
				shippingPhoneNo = '';
			} else if (shippingPhoneNo.length > 0 && shippingPhoneNo.length < 4) {
				shippingPhoneNo = `(${shippingPhoneNo}`;
			} else if (shippingPhoneNo.length > 3 && shippingPhoneNo.length < 7) {
				shippingPhoneNo = `(${shippingPhoneNo.slice(0, 3)}) ${shippingPhoneNo.slice(3, 6)}`;
			} else if (shippingPhoneNo.length >= 7) {
				shippingPhoneNo = `(${shippingPhoneNo.slice(0, 3)}) ${shippingPhoneNo.slice(
					3,
					6
				)}-${shippingPhoneNo.slice(6, 10)}`;
			}

			if (value[cursorPosition]) {
				setTimeout(() => {
					phoneRef.current && phoneRef.current.setSelectionRange(cursorPosition, cursorPosition);
				}, 0);
			}
			if (
				event.nativeEvent.inputType !== 'deleteContentBackward' &&
				value[cursorPosition] === ')'
			) {
				setTimeout(() => {
					phoneRef.current &&
						phoneRef.current.setSelectionRange(cursorPosition + 2, cursorPosition + 2);
				}, 0);
			}
			if (
				event.nativeEvent.inputType !== 'deleteContentBackward' &&
				(value[cursorPosition] === '(' || value[cursorPosition] === '-')
			) {
				setTimeout(() => {
					phoneRef.current &&
						phoneRef.current.setSelectionRange(cursorPosition + 1, cursorPosition + 1);
				}, 0);
			}
			if (
				event.nativeEvent.inputType === 'deleteContentBackward' ||
				event.nativeEvent.inputType === 'deleteContentForward'
			) {
				setTimeout(() => {
					phoneRef.current && phoneRef.current.setSelectionRange(cursorPosition, cursorPosition);
				}, 0);
			}

			setContactForm({ ...contactForm, [name]: shippingPhoneNo });
		}
		if (name === 'streetAddress' && value) {
			setFormErrorMessages({ ...formErrorMessages, isAddressValid: true });
		}
		if (name === 'streetAddress' && !value) {
			setAddressForm({
				...addressForm,
				streetAddress: '',
				additionalAddress: '',
				city: '',
				state: '',
				postalCode: ''
			});
		}

		if (!value && name !== 'additionalAddress') {
			setIsSaveButtonDisabled(true);
		}
	};

	const enableSaveButton = () => {
		const { ...newAddressForm }: any = addressForm;

		delete newAddressForm.additionalAddress;

		const addressFormValid = Object.values(newAddressForm).every((value) => value !== '');
		const contactFormValid = Object.values(contactForm).every((value) => value !== '');

		if (addressFormValid && contactFormValid && formErrorMessages.isAddressValid) {
			setIsSaveButtonDisabled(false);
		} else {
			setIsSaveButtonDisabled(true);
		}
	};

	const populateAddress = (address: any) => {
		if (address.Province === 'AK' || address.Province === 'HI') {
			setFormErrorMessages({ ...formErrorMessages, isAddressValid: false });
			setIsSaveButtonDisabled(true);
			if (addressLineOneRef.current) {
				addressLineOneRef.current.focus();
			}
		} else {
			setAddressForm({
				...addressForm,
				streetAddress: address.Line1,
				additionalAddress: address.Line2,
				city: address.City,
				state: address.Province,
				postalCode: address.PostalCode
			});

			if (!address.Line2 && addressLineTwoRef.current) {
				addressLineTwoRef.current.classList.remove('active');
			}
		}
	};

	const onCloseShippingAddressModal = () => {
		setIsShippingAddInfoModalOpen(false);

		setFormErrorMessages({
			...formErrorMessages,
			isPhoneNoValid: true,
			isAddressValid: true
		});

		setContactForm({
			firstName: shippingAddress ? shippingAddress.firstName : '',
			lastName: shippingAddress ? shippingAddress.lastName : '',
			phone: shippingAddress ? shippingAddress.phone : ''
		});

		setAddressForm({
			streetAddress: shippingAddress ? shippingAddress.streetAddress : '',
			additionalAddress: shippingAddress ? shippingAddress.additionalAddress : '',
			city: shippingAddress ? shippingAddress.city : '',
			state: shippingAddress ? shippingAddress.state : '',
			postalCode: shippingAddress ? shippingAddress.postalCode : ''
		});

		if (shippingAddress) {
			setIsSaveButtonDisabled(false);
		} else {
			setIsSaveButtonDisabled(true);
		}
	};

	const handleSubmit = (event: any) => {
		event.preventDefault();
		logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_SHIPPING_ADDRESS_UPDATE');

		if (contactForm.phone.length < 14) {
			setFormErrorMessages({ ...formErrorMessages, isPhoneNoValid: false });
			setIsSaveButtonDisabled(true);
			if (phoneRef.current) {
				phoneRef.current.focus();
			}
		} else {
			callUpdateShippingAddressAPI();
		}
	};

	const callUpdateShippingAddressAPI = () => {
		setIsSpinnerLoading(true);

		const shippingAddressForm: ShippingAddressPayload = {
			firstName: contactForm.firstName,
			lastName: contactForm.lastName,
			phone: contactForm.phone,
			streetAddress: addressForm.streetAddress,
			additionalAddress: addressForm.additionalAddress,
			city: addressForm.city,
			state: addressForm.state,
			postalCode: addressForm.postalCode
		};

		requestRedwolfService(authAPI.updateShippingAddress(token, shippingAddressForm))
			.then((response: any) => {
				setIsSpinnerLoading(false);
				const consentStatus = response.constructor.$metadata$.simpleName;

				if (consentStatus === 'Success' && response.data) {
					logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_SHIPPING_ADDRESS_UPDATE_SUCCESS');
					dispatch(setShippingAddress(response.data));
					setIsShippingAddInfoModalOpen(false);

					dispatch(
						openNotification({
							type: 'SUCCESS',
							heading: 'Success',
							body: notification.message.shippingAddressSuccess
						})
					);
				} else {
					logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_SHIPPING_ADDRESS_UPDATE_FAILURE', {
						error: response
					});
					Sentry.captureException(new Error(`updateShippingAddress: ${response}`));

					dispatch(
						openNotification({
							type: 'ERROR',
							heading: 'Error',
							body: notification.message.shippingAddressError
						})
					);
				}
				transaction && transaction.end();
			})
			.catch((err: any) => {
				setIsSpinnerLoading(false);
				showTechnicalErrorMessage();
				transaction && transaction.end();
				Sentry.captureException(err);
				logGoogleAnalyticsEvent('VIZIO_ACCOUNT_WEB_SHIPPING_ADDRESS_UPDATE_FAILURE', {
					error: err
				});
			});
	};

	if (firstNameRef.current && firstNameRef.current.value) {
		firstNameRef.current.classList.add('active');
	}
	if (lastNameRef.current && lastNameRef.current.value) {
		lastNameRef.current.classList.add('active');
	}
	if (phoneRef.current && phoneRef.current.value) {
		phoneRef.current.classList.add('active');
	}
	if (addressLineOneRef.current && addressLineOneRef.current.value) {
		addressLineOneRef.current.classList.add('active');
	}
	if (addressLineTwoRef.current && addressLineTwoRef.current.value) {
		addressLineTwoRef.current.classList.add('active');
	} else if (addressLineTwoRef.current) {
		addressLineTwoRef.current.classList.remove('active');
	}
	if (cityRef.current && cityRef.current.value) {
		cityRef.current.classList.add('active');
	} else if (cityRef.current) {
		cityRef.current.classList.remove('active');
	}
	if (stateRef.current && stateRef.current.value) {
		stateRef.current.classList.add('active');
	} else if (stateRef.current) {
		stateRef.current.classList.remove('active');
	}
	if (zipCodeRef.current && zipCodeRef.current.value) {
		zipCodeRef.current.classList.add('active');
	} else if (zipCodeRef.current) {
		zipCodeRef.current.classList.remove('active');
	}

	return (
		<div className="contact-info-area ship-adrss-ctr">
			<div className="contact-info-area-left">
				<h2 className="contact-info" id="edit-shipping-address">
					Shipping Address
				</h2>
				{shippingAddress && (
					<address className="shipping-address">
						<span>{`${shippingAddress.firstName} ${shippingAddress.lastName}`} </span>
						<span>{shippingAddress.phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')}</span>
						<span>{`${shippingAddress.streetAddress} ${shippingAddress.additionalAddress}`}</span>
						<span>{`${shippingAddress.city}, ${shippingAddress.state} ${shippingAddress.postalCode}`}</span>
					</address>
				)}

				{isSkeletonLoading && <AccountSkeleton />}

				<p id="empty-address" hidden>
					Select Edit to add a shipping address.
				</p>
			</div>
			<div className="contact-info-area-right">
				<button
					className="primary-button acc-info-edit"
					aria-describedby="edit-shipping-address"
					onClick={() => setIsShippingAddInfoModalOpen(true)}>
					Edit
				</button>

				<Modal
					containerId="shipModal"
					containerClasses="shipping-modal"
					show={isShippingAddInfoModalOpen}
					onHide={onCloseShippingAddressModal}
					heading="Shipping Address">
					<form className="edit-modal-form" id="update-shipping-acc-dashboard">
						<p className="shipadrpopup-subtitle">Currently we do not ship to Hawaii or Alaska.</p>
						<section>
							<div className="form-field form-field-group">
								<div className="field half-filed">
									<div className="input-box form-group input-label">
										<input
											type="text"
											className="form-control"
											name="firstName"
											id="shipping_first_name"
											aria-required="true"
											autoComplete="given-name"
											aria-describedby="shipping_first_name_req"
											spellCheck="false"
											value={contactForm.firstName}
											onChange={handleChange}
											onKeyDown={handleKeyDown}
											ref={firstNameRef}
										/>
										<label htmlFor="shipping_first_name" className="control-label">
											First Name
										</label>
										<code id="shipping_first_name_req"></code>
									</div>
								</div>
								<div className="field half-filed">
									<div className="input-box form-group input-label">
										<input
											type="text"
											className="form-control"
											name="lastName"
											id="shipping_last_name"
											aria-required="true"
											autoComplete="family-name"
											aria-describedby="shipping_last_name_req"
											spellCheck="false"
											value={contactForm.lastName}
											onChange={handleChange}
											onKeyDown={handleKeyDown}
											ref={lastNameRef}
										/>
										<label htmlFor="shipping_last_name" className="control-label">
											Last Name
										</label>
										<code id="shipping_last_name_req"></code>
									</div>
								</div>
							</div>
							<div className={`${!formErrorMessages.isPhoneNoValid ? 'field has-error' : 'field'}`}>
								<div className="input-box form-group input-label">
									<input
										type="text"
										className="form-control"
										name="phone"
										id="shipping_phone"
										aria-required="true"
										autoComplete="tel"
										aria-describedby="shipping_phone_req"
										aria-label="Phone Number"
										value={contactForm.phone}
										onChange={handleChange}
										onKeyDown={handleKeyDown}
										onKeyPress={restrictPhoneNo}
										maxLength={14}
										ref={phoneRef}
									/>
									<label htmlFor="shipping_phone" className="control-label">
										Phone Number
									</label>
									<code id="shipping_phone_req">
										{!formErrorMessages.isPhoneNoValid ? 'Enter valid phone number' : ''}
									</code>
								</div>
							</div>

							<div className={`${!formErrorMessages.isAddressValid ? 'field has-error' : 'field'}`}>
								<div className="input-box form-group input-label">
									<input
										type="text"
										className="form-control"
										name="streetAddress"
										id="shipping_address_search"
										autoComplete="address-line1"
										aria-required="true"
										aria-describedby="shipping_address_line_one"
										aria-label="Address 1"
										spellCheck="false"
										value={addressForm.streetAddress}
										onChange={handleChange}
										ref={addressLineOneRef}
									/>
									<label htmlFor="shipping_address_search" className="control-label">
										{' '}
										Address 1
									</label>
									<code id="shipping_address_line_one">
										{!formErrorMessages.isAddressValid
											? 'Sorry !! Currently we do not ship to Hawaii/Alaska. Please choose a different address.'
											: ''}
									</code>
								</div>
							</div>

							<div className="field">
								<div className="input-box form-group input-label">
									<input
										type="text"
										className="form-control"
										name="additionalAddress"
										id="shipping_apartment"
										autoComplete="address-line2"
										aria-label="Address 2"
										spellCheck="false"
										value={addressForm.additionalAddress}
										onChange={handleChange}
										ref={addressLineTwoRef}
									/>
									<label htmlFor="shipping_apartment" className="control-label">
										{' '}
										Address 2
									</label>
								</div>
							</div>

							<div className="form-field form-field-group col-row">
								<div className="field col-6 ship-ml-6">
									<div className="input-box form-group input-label">
										<input
											type="text"
											className="form-control disable"
											name="city"
											id="shipping_city"
											aria-required="true"
											autoComplete="address-line3"
											aria-describedby="shipping_city_req"
											aria-label="City"
											spellCheck="false"
											value={addressForm.city}
											ref={cityRef}
											readOnly
										/>
										<label htmlFor="shipping_city" className="control-label">
											{' '}
											City
										</label>
										<code id="shipping_city_req"></code>
									</div>
								</div>
								<div className="field col-3 ship-ml-2">
									<div className="input-box form-group input-label">
										<input
											type="text"
											className="form-control disable"
											name="state"
											id="shipping_state"
											aria-required="true"
											autoComplete="address-level1"
											aria-describedby="shipping_state_req"
											aria-label="State"
											spellCheck="false"
											value={addressForm.state}
											ref={stateRef}
											readOnly
										/>
										<label htmlFor="shipping_state" className="control-label">
											State
										</label>
										<code id="shipping_state_req"></code>
									</div>
								</div>
								<div className="field col-3 ship-ml-4">
									<div className="input-box form-group input-label">
										<input type="hidden" name="country" id="country" />
										<input
											type="text"
											className="form-control disable"
											name="postalCode"
											id="shipping_zipcode"
											aria-required="true"
											autoComplete="postal-code"
											aria-describedby="shipping_zip_req"
											aria-label="ZIP Code"
											spellCheck="false"
											value={addressForm.postalCode}
											ref={zipCodeRef}
											readOnly
										/>
										<label htmlFor="shipping_zipcode" className="control-label">
											{' '}
											ZIP Code
										</label>
										<code id="shipping_zip_req"></code>
									</div>
								</div>
							</div>
						</section>
						<div className="form-field content-right">
							<button
								type="submit"
								onClick={handleSubmit}
								className="primary-button"
								value="SAVE"
								disabled={isSaveButtonDisabled}>
								Save
							</button>
						</div>
					</form>
				</Modal>

				{isSpinnerLoading && <Spinner />}
			</div>
		</div>
	);
};

export default ShippingAddress;
